import { useSetAtom } from 'jotai'
import React from 'react'
import type { Transaction } from '.'
import { transactionsActionsAtom } from './transaction-storage'

interface TransactionContextProps {
  addTransaction: (transaction: Transaction) => void
  updateTransaction: (transaction: Transaction) => void
  clearTransactions: () => void
}

const TransactionContext = React.createContext<TransactionContextProps>({
  addTransaction: () => undefined,
  updateTransaction: () => undefined,
  clearTransactions: () => undefined,
})

const useTransactionService = (): TransactionContextProps => {
  const { addTransaction, clearTransactions, updateTransaction } =
    React.useContext(TransactionContext)
  return {
    addTransaction,
    clearTransactions,
    updateTransaction,
  }
}

// This context maintain 2 counters that can be used as a dependencies on other hooks to force a periodic refresh
const TransactionContextProvider = ({
  children,
}: {
  children: JSX.Element[] | JSX.Element
}): JSX.Element => {
  const dispatch = useSetAtom(transactionsActionsAtom)

  const addTransaction = (transaction: Transaction) => {
    dispatch({
      type: 'add',
      payload: transaction,
    })
  }

  const updateTransaction = (transaction: Transaction) => {
    dispatch({
      type: 'update',
      payload: transaction,
    })
  }

  const clearTransactions = () => dispatch({ type: 'delete' })

  return (
    <TransactionContext.Provider value={{ addTransaction, clearTransactions, updateTransaction }}>
      {children}
    </TransactionContext.Provider>
  )
}

export { TransactionContext, TransactionContextProvider, useTransactionService }
